import { useState, useMemo } from "react";
import {
  Sun, Zap, Leaf, Calculator, ArrowRight,
  BatteryCharging, BatteryMedium, Gauge, PanelTop,
  Flame, Car, TrendingDown, Award, Plug,
  Home, Building2, Store, BarChart3,
} from "lucide-react";

const SOLAR_IRRADIANCE = {
  "Abruzzo": 1350, "Basilicata": 1500, "Calabria": 1550, "Campania": 1450,
  "Emilia-Romagna": 1250, "Friuli Venezia Giulia": 1200, "Lazio": 1400,
  "Liguria": 1300, "Lombardia": 1200, "Marche": 1300, "Molise": 1400,
  "Piemonte": 1200, "Puglia": 1550, "Sardegna": 1600, "Sicilia": 1650,
  "Toscana": 1350, "Trentino-Alto Adige": 1150, "Umbria": 1350,
  "Valle d'Aosta": 1150, "Veneto": 1220,
};

const ELECTRICITY_PRICE = 0.25;
const CO2_PER_KWH = 0.35;
const ANNUAL_DEGRADATION = 0.005;
const PANEL_WATT = 450;
const BALCONY_WATT = 820;
const BALCONY_SELF_CONSUMPTION = 0.70;

const regions = Object.keys(SOLAR_IRRADIANCE).sort();

const sectorTabs = [
  { id: "residenziale", label: "Residenziale", icon: Home, color: "emerald" },
  { id: "balcone", label: "Balcone", icon: Sun, color: "amber" },
  { id: "pmi", label: "PMI", icon: Store, color: "violet" },
];

const tabColors = {
  emerald: { active: "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30", text: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-500", accent: "from-emerald-400 to-emerald-500", slider: "#00C853" },
  blue: { active: "bg-blue-500 text-white shadow-lg shadow-blue-500/30", text: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-500", accent: "from-blue-400 to-blue-500", slider: "#2196F3" },
  amber: { active: "bg-amber-500 text-white shadow-lg shadow-amber-500/30", text: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-500", accent: "from-amber-400 to-amber-500", slider: "#FF9800" },
  violet: { active: "bg-violet-500 text-white shadow-lg shadow-violet-500/30", text: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200", badge: "bg-violet-500", accent: "from-violet-400 to-violet-500", slider: "#7C3AED" },
};

function getRecommendation(sectorId, systemSizeKw) {
  if (sectorId === "balcone") {
    return { name: "Kit Balcone Plug & Play", panels: 2, panelType: "Monocristallino 410W", battery: "Nessuna", batteryType: "Autoconsumo diretto", inverter: "Micro-inverter 800W", icon: Plug };
  }
  if (sectorId === "residenziale") {
    if (systemSizeKw <= 3.5) return { name: "Kit Residenziale Base", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Monocristallino 450W", battery: "5.0 kWh", batteryType: "Litio LFP", inverter: "Ibrido 3.6 kW", icon: Sun };
    if (systemSizeKw <= 6) return { name: "Kit Residenziale Plus", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Monocristallino 450W", battery: "10.0 kWh", batteryType: "Litio LFP", inverter: "Ibrido 6 kW", icon: BatteryCharging };
    return { name: "Kit Residenziale Premium", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Monocristallino 450W N-Type", battery: "15.0 kWh", batteryType: "Litio LFP Alta Capacita", inverter: "Ibrido 10 kW", icon: Flame };
  }
  if (sectorId === "aziende") {
    if (systemSizeKw <= 30) return { name: "Impianto Commerciale", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Bifacciale 450W N-Type", battery: "30 kWh", batteryType: "Litio LFP Modulare", inverter: `Stringa ${Math.ceil(systemSizeKw)} kW`, icon: Building2 };
    if (systemSizeKw <= 100) return { name: "Impianto Industriale", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Bifacciale 450W N-Type", battery: "100 kWh", batteryType: "Container Litio LFP", inverter: `Centralizzato ${Math.ceil(systemSizeKw)} kW`, icon: Flame };
    return { name: "Impianto Industriale XL", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Bifacciale 450W N-Type", battery: "200+ kWh", batteryType: "Container LFP Modulare", inverter: `Multi-inverter ${Math.ceil(systemSizeKw)} kW`, icon: Car };
  }
  if (systemSizeKw <= 10) return { name: "Kit PMI Compact", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Monocristallino 450W", battery: "10 kWh", batteryType: "Litio LFP", inverter: `Ibrido ${Math.ceil(systemSizeKw)} kW`, icon: Store };
  return { name: "Kit PMI Business", panels: Math.ceil(systemSizeKw * 1000 / PANEL_WATT), panelType: "Monocristallino 450W N-Type", battery: "20 kWh", batteryType: "Litio LFP Modulare", inverter: `Ibrido ${Math.ceil(systemSizeKw)} kW`, icon: Flame };
}

function calculate(sectorId, inputs, region) {
  const irradiance = SOLAR_IRRADIANCE[region] || 1350;

  if (sectorId === "balcone") {
    const annualProduction = Math.round(BALCONY_WATT / 1000 * irradiance);
    const selfConsumed = Math.round(annualProduction * BALCONY_SELF_CONSUMPTION);
    const annualSavings = Math.round(selfConsumed * ELECTRICITY_PRICE);
    let total20 = 0;
    for (let y = 0; y < 20; y++) total20 += annualSavings * Math.pow(1 - 0.004, y);
    const co2 = Math.round(annualProduction * CO2_PER_KWH / 1000 * 10) / 10;
    const rec = getRecommendation("balcone", 0.82);
    return {
      isBalcony: true,
      systemSize: 0.82,
      annualProduction,
      selfConsumed,
      annualBill: inputs.monthlyBill * 12,
      noBatt: { annual: annualSavings, total25: Math.round(total20) },
      withBatt: { annual: annualSavings, total25: Math.round(total20) },
      extraSavings: 0,
      co2,
      recommendation: rec,
      payback: 0,
      monthlyAvg: Math.round(annualSavings / 12),
    };
  }

  let annualBill, selfNoBatt, selfWithBatt;

  if (sectorId === "residenziale") {
    annualBill = inputs.monthlyBill * 12;
    selfNoBatt = 0.55;
    selfWithBatt = 0.90;
  } else {
    annualBill = inputs.monthlyBill * 12;
    selfNoBatt = 0.50;
    selfWithBatt = 0.85;
  }

  const annualConsumption = annualBill / ELECTRICITY_PRICE;
  const systemSize = Math.ceil(annualConsumption / irradiance * 10) / 10;
  const annualProduction = systemSize * irradiance;

  const savingsNoBatt = Math.round(annualProduction * selfNoBatt * ELECTRICITY_PRICE);
  let total25NoBatt = 0;
  for (let y = 0; y < 25; y++) total25NoBatt += savingsNoBatt * Math.pow(1 - ANNUAL_DEGRADATION, y);

  const savingsWithBatt = Math.round(annualProduction * selfWithBatt * ELECTRICITY_PRICE);
  let total25WithBatt = 0;
  for (let y = 0; y < 25; y++) total25WithBatt += savingsWithBatt * Math.pow(1 - ANNUAL_DEGRADATION, y);

  const co2 = Math.round(annualProduction * CO2_PER_KWH / 1000 * 10) / 10;
  const rec = getRecommendation(sectorId, systemSize);

  const estimatedCost = systemSize * 1100;
  const payback = Math.round(estimatedCost / savingsWithBatt * 10) / 10;

  return {
    systemSize: Math.round(systemSize * 10) / 10,
    annualProduction: Math.round(annualProduction),
    annualBill,
    noBatt: { annual: savingsNoBatt, total25: Math.round(total25NoBatt) },
    withBatt: { annual: savingsWithBatt, total25: Math.round(total25WithBatt) },
    extraSavings: savingsWithBatt - savingsNoBatt,
    co2,
    recommendation: rec,
    payback,
  };
}

export const SavingsCalculator = ({ onConfigureClick, initialSector }) => {
  const [activeSector, setActiveSector] = useState(initialSector || "residenziale");
  const [region, setRegion] = useState("Lazio");
  const [inputs, setInputs] = useState({
    monthlyBill: 120,
    annualBill: 25000,
  });

  const locked = false;
  const visibleTabs = initialSector === "residenziale"
    ? sectorTabs.filter((t) => t.id === "residenziale" || t.id === "balcone")
    : initialSector === "pmi"
    ? sectorTabs.filter((t) => t.id === "pmi")
    : sectorTabs;
  const tab = sectorTabs.find((t) => t.id === activeSector);
  const tc = tabColors[tab.color];

  const results = useMemo(() => calculate(activeSector, inputs, region), [activeSector, inputs, region]);
  const RecIcon = results.recommendation.icon;
  const labels = results.customLabels || {
    noBattTitle: "Solo Pannelli",
    noBattDesc: `Autoconsumo ~${activeSector === "pmi" ? "50" : "55"}%`,
    withBattTitle: "Pannelli + Batteria",
    withBattDesc: `Autoconsumo ~${activeSector === "pmi" ? "85" : "90"}%`,
    savingsLabel: "Risparmio Annuo",
  };

  return (
    <section className="py-20 sm:py-28 bg-slate-50 relative overflow-hidden" data-testid="savings-calculator-section">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-100/40 rounded-full -translate-y-1/3 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-100/30 rounded-full translate-y-1/2 -translate-x-1/3" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-left max-w-xl mb-10">
          <span className={`${tc.text} font-semibold text-sm uppercase tracking-wide`}>Calcolatore Risparmio</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="calculator-title">
            Calcola il risparmio per<br />il tuo settore
          </h2>
          <p className="mt-4 text-slate-500 text-base sm:text-lg">
            Seleziona il settore, la spesa energetica e la regione per una stima personalizzata.
          </p>
        </div>

        {/* Sector Tabs */}
        {!locked && (
          <div className="flex flex-wrap gap-2 mb-8 p-1 bg-white rounded-2xl border border-slate-200 shadow-sm w-fit" data-testid="calculator-sector-tabs">
            {visibleTabs.map((s) => {
              const active = activeSector === s.id;
              const stc = tabColors[s.color];
              return (
                <button key={s.id} onClick={() => setActiveSector(s.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${active ? stc.active : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
                  data-testid={`calc-tab-${s.id}`}>
                  <s.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span>{s.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Controls Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" data-testid="calculator-controls">
          {/* Input */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-sm">
            {activeSector === "residenziale" && (
              <>
                <div className="flex items-center justify-between mb-5">
                  <label className="text-sm font-bold text-slate-700">Bolletta Mensile</label>
                  <span className={`text-3xl font-bold ${tc.text} stat-number`} style={{ fontFamily: "Outfit, sans-serif" }} data-testid="bill-display">{"\u20AC"}{inputs.monthlyBill}</span>
                </div>
                <input type="range" min={30} max={500} step={10} value={inputs.monthlyBill}
                  onChange={(e) => setInputs({ ...inputs, monthlyBill: Number(e.target.value) })}
                  className="w-full h-2.5 bg-slate-200 rounded-full appearance-none cursor-pointer" style={{ accentColor: tc.slider }} data-testid="bill-slider" />
                <div className="flex justify-between text-xs text-slate-400 mt-2"><span>{"\u20AC"}30</span><span>{"\u20AC"}500</span></div>
              </>
            )}
            {activeSector === "balcone" && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Plug className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
                </div>
                <h4 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Kit Balcone 820W</h4>
                <p className="text-sm text-slate-500 mb-4">Plug & Play | 2 Pannelli | Micro-inverter</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-amber-50 rounded-xl p-3">
                    <div className="text-lg font-bold text-amber-600" style={{ fontFamily: "Outfit, sans-serif" }}>820W</div>
                    <div className="text-[10px] text-slate-500">Potenza</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3">
                    <div className="text-lg font-bold text-amber-600" style={{ fontFamily: "Outfit, sans-serif" }}>~70%</div>
                    <div className="text-[10px] text-slate-500">Autoconsumo</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3">
                    <div className="text-lg font-bold text-amber-600" style={{ fontFamily: "Outfit, sans-serif" }}>25+</div>
                    <div className="text-[10px] text-slate-500">Anni vita</div>
                  </div>
                </div>
              </div>
            )}
            {activeSector === "pmi" && (
              <>
                <div className="flex items-center justify-between mb-5">
                  <label className="text-sm font-bold text-slate-700">Bolletta Mensile</label>
                  <span className={`text-3xl font-bold ${tc.text} stat-number`} style={{ fontFamily: "Outfit, sans-serif" }} data-testid="bill-display">{"\u20AC"}{inputs.monthlyBill}</span>
                </div>
                <input type="range" min={100} max={3000} step={50} value={inputs.monthlyBill}
                  onChange={(e) => setInputs({ ...inputs, monthlyBill: Number(e.target.value) })}
                  className="w-full h-2.5 bg-slate-200 rounded-full appearance-none cursor-pointer" style={{ accentColor: tc.slider }} data-testid="bill-slider" />
                <div className="flex justify-between text-xs text-slate-400 mt-2"><span>{"\u20AC"}100</span><span>{"\u20AC"}3.000</span></div>
              </>
            )}
          </div>

          {/* Region */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-sm">
            <label className="text-sm font-bold text-slate-700 mb-3 block">La Tua Regione</label>
            <select value={region} onChange={(e) => setRegion(e.target.value)}
              className="flex h-12 w-full rounded-xl border border-input bg-transparent px-4 py-1 text-base shadow-sm input-focus-ring" data-testid="calculator-region-select">
              {regions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-3">
              Irraggiamento: <span className="font-semibold text-slate-600">{SOLAR_IRRADIANCE[region]} kWh/kWp/anno</span>
            </p>
            {results.payback > 0 && (
              <div className={`mt-4 ${tc.bg} rounded-xl p-3 flex items-center gap-3`}>
                <BarChart3 className={`w-5 h-5 ${tc.text}`} />
                <div>
                  <div className="text-xs text-slate-500">Tempo di Rientro Stimato</div>
                  <div className={`text-lg font-bold ${tc.text}`} style={{ fontFamily: "Outfit, sans-serif" }}>{results.payback} anni</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results: Balcone specific or standard */}
        {results.isBalcony ? (
          <>
            {/* Balcony Results */}
            <div className="bg-white rounded-2xl border-2 border-amber-200 p-6 sm:p-8 mb-8 shadow-sm" data-testid="balcony-results-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Plug className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Kit Balcone Plug & Play</h3>
                  <p className="text-sm text-slate-500">820W | Autoconsumo diretto ~70%</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <Sun className="w-5 h-5 text-amber-500 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-calc-production">
                    {results.annualProduction.toLocaleString("it-IT")}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">kWh/anno</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <Zap className="w-5 h-5 text-emerald-500 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-calc-annual">
                    {"\u20AC"}{results.noBatt.annual}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">risparmio/anno</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <TrendingDown className="w-5 h-5 text-blue-500 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-calc-total">
                    {"\u20AC"}{results.noBatt.total25.toLocaleString("it-IT")}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">risparmio 20 anni</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <Leaf className="w-5 h-5 text-green-500 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-2xl font-bold text-green-600" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-calc-co2">
                    {results.co2 > 0 ? results.co2.toFixed(1) : "<0.1"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">ton CO{"\u2082"}/anno</div>
                </div>
              </div>
              <div className="mt-5 bg-slate-50 rounded-xl p-4 flex items-center justify-around">
                <div className="text-center">
                  <div className="text-xs text-slate-400">Risparmio mensile</div>
                  <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{"\u20AC"}{results.monthlyAvg}/mese</div>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div className="text-center">
                  <div className="text-xs text-slate-400">Energia autoconsumata</div>
                  <div className="text-lg font-bold text-amber-600" style={{ fontFamily: "Outfit, sans-serif" }}>{results.selfConsumed} kWh</div>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                * Stime basate su autoconsumo ~70%, {"\u20AC"}0,25/kWh, degradazione 0,4%/anno. Risultati variabili in base a esposizione e abitudini.
              </p>
            </div>
          </>
        ) : (
          <>
        {/* Recommended System */}
        <div className={`bg-white rounded-2xl border-2 ${tc.border} p-6 sm:p-8 mb-8 shadow-sm relative overflow-hidden`} data-testid="recommended-system">
          <div className={`absolute top-0 right-0 ${tc.badge} text-white text-xs font-bold px-5 py-2 rounded-bl-2xl flex items-center gap-1.5`}>
            <Award className="w-3.5 h-3.5" /> Impianto Consigliato
          </div>
          <div className="flex items-center gap-3 mb-6 mt-2">
            <div className={`w-12 h-12 rounded-xl ${tc.bg} flex items-center justify-center`}>
              <RecIcon className={`w-6 h-6 ${tc.text}`} strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{results.recommendation.name}</h3>
              <p className="text-sm text-slate-500">Configurazione ottimale per il tuo profilo</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><PanelTop className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pannelli</span></div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{results.recommendation.panels}</div>
              <div className="text-xs text-slate-400 mt-0.5">{results.recommendation.panelType}</div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><Gauge className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Potenza</span></div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{results.systemSize}<span className="text-sm font-medium text-slate-400 ml-1">kWp</span></div>
              <div className="text-xs text-slate-400 mt-0.5">{results.annualProduction.toLocaleString("it-IT")} kWh/anno</div>
            </div>
            <div className={`${tc.bg} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-2"><BatteryCharging className={`w-4 h-4 ${tc.text}`} strokeWidth={1.5} /><span className={`text-xs font-semibold ${tc.text} uppercase tracking-wide`}>Batteria</span></div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{results.recommendation.battery}</div>
              <div className="text-xs text-slate-500 mt-0.5">{results.recommendation.batteryType}</div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><Zap className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Inverter</span></div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{results.recommendation.inverter.split(" ").slice(-2).join(" ")}</div>
              <div className="text-xs text-slate-400 mt-0.5">{results.recommendation.inverter.split(" ")[0]}</div>
            </div>
          </div>
        </div>

        {/* Savings Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" data-testid="calculator-comparison">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-sm" data-testid="compare-no-batt">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><BatteryMedium className="w-5 h-5 text-blue-600" strokeWidth={1.5} /></div>
              <div><h4 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{labels.noBattTitle}</h4><p className="text-xs text-slate-400">{labels.noBattDesc}</p></div>
            </div>
            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-0.5">{labels.savingsLabel}</div>
              <div className="text-3xl font-bold text-blue-600 stat-number" style={{ fontFamily: "Outfit, sans-serif" }}>{"\u20AC"}{results.noBatt.annual.toLocaleString("it-IT")}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-xs text-slate-400">In 25 anni</div>
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{"\u20AC"}{results.noBatt.total25.toLocaleString("it-IT")}</div>
            </div>
          </div>
          <div className={`bg-white rounded-2xl border-2 ${tc.border} p-6 sm:p-7 shadow-sm relative`} data-testid="compare-with-batt">
            <div className={`absolute -top-px -right-px ${tc.badge} text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl rounded-tr-xl`}>
              +{"\u20AC"}{results.extraSavings.toLocaleString("it-IT")}/{results.extraLabel || "anno"}
            </div>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-xl ${tc.bg} flex items-center justify-center`}><BatteryCharging className={`w-5 h-5 ${tc.text}`} strokeWidth={1.5} /></div>
              <div><h4 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{labels.withBattTitle}</h4><p className="text-xs text-slate-400">{labels.withBattDesc}</p></div>
            </div>
            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-0.5">{labels.savingsLabel}</div>
              <div className={`text-3xl font-bold ${tc.text} stat-number`} style={{ fontFamily: "Outfit, sans-serif" }}>{"\u20AC"}{results.withBatt.annual.toLocaleString("it-IT")}</div>
            </div>
            <div className={`${tc.bg} rounded-lg p-3`}>
              <div className={`text-xs ${tc.text}`}>In 25 anni</div>
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{"\u20AC"}{results.withBatt.total25.toLocaleString("it-IT")}</div>
            </div>
          </div>
        </div>

        {/* Eco Stats */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm mb-8" data-testid="savings-visual">
          <h4 className="text-sm font-bold text-slate-700 mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>Impatto Ambientale e Produzione</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <Leaf className="w-5 h-5 text-emerald-500 mx-auto mb-1" strokeWidth={1.5} />
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{results.co2} ton</div>
              <div className="text-xs text-slate-400">CO{"\u2082"} risparmiata/anno</div>
            </div>
            <div>
              <TrendingDown className={`w-5 h-5 ${tc.text} mx-auto mb-1`} strokeWidth={1.5} />
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                {results.annualBill ? `${Math.round((results.withBatt.annual / results.annualBill) * 100)}%` : "-"}
              </div>
              <div className="text-xs text-slate-400">Riduzione bolletta</div>
            </div>
            <div>
              <Sun className="w-5 h-5 text-amber-500 mx-auto mb-1" strokeWidth={1.5} />
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{results.annualProduction.toLocaleString("it-IT")}</div>
              <div className="text-xs text-slate-400">kWh prodotti/anno</div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-5 leading-relaxed">
            * Stime indicative basate su irraggiamento regionale. I risultati effettivi dipendono da orientamento, inclinazione e ombreggiamenti.
          </p>
        </div>
          </>
        )}

        <div className="text-center">
          <button onClick={onConfigureClick}
            className={`${tc.badge} hover:opacity-90 text-white font-bold rounded-full px-10 py-4 text-base btn-scale shadow-xl inline-flex items-center gap-2`}
            data-testid="calculator-cta">
            <Calculator className="w-5 h-5" /> Richiedi Preventivo Personalizzato <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};
